import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  ArraySortPipe,
  ControlErrorDirective,
  LabelValueComponent,
  UIBlockerService
} from '@howdeniberia/core-front';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { YES_NO_VALUES } from 'src/app/core/constants';
import {
  ICalculatorAAxaRequest,
  ICalculatorAPrebalRequest,
  ICalculatorASurneRequest,
  IResponseMessage
} from 'src/app/core/models/api/sane-project/arpa-model';
import { CalculatorProjectService } from 'src/app/core/services/api/calculator/calculator-project.service';
import { BonusPipe } from 'src/app/core/services/api/shared/bonus.pipe';
import { SharedService } from 'src/app/core/services/api/shared/shared.service';
import { normalizeTraceHtml, toNumber } from 'src/app/shared/utils';
import { ProjectViewModel } from '../../models/project.viewmodel';
import { CalculatorMapper } from '../mappers/calculator.mapper';
import {
  CalculatorProductViewModel,
  accidentPresets
} from '../models/calculator-product.viewmodel';
import { NumberMaxDirective } from '../models/number-max.directive';

interface IEditRuntimeMinimal {
  projectClientProfessionCode?: string | number;
  projectActivity?: string;
  projectClientId?: string | number;
}

@Component({
  selector: 'howden-calculator-accidents',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatDividerModule,
    ArraySortPipe,
    ControlErrorDirective,
    LabelValueComponent,
    BonusPipe,
    NumberMaxDirective
  ],
  templateUrl: './calculator-accidents.component.html',
  styleUrls: ['./calculator-accidents.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CalculatorAccidentsComponent implements OnDestroy {
  private fb = inject(FormBuilder);
  private uiBlocker = inject(UIBlockerService);
  private calcSrv = inject(CalculatorProjectService);
  private sharedSrv = inject(SharedService);
  private cdr = inject(ChangeDetectorRef);
  private sanitizer = inject(DomSanitizer);

  form: FormGroup;
  accidentsAxa = new CalculatorProductViewModel(accidentPresets['axa']);
  accidentsSurne = new CalculatorProductViewModel(accidentPresets['surne']);
  accidentsPrebal = new CalculatorProductViewModel(accidentPresets['prebal']);
  project!: ProjectViewModel;

  showHivAxa = false;
  showHivSurne = false;
  showHivPrebal = false;

  private destroy$ = new Subject<void>();

  compareByIdNumber = this.calcSrv.compareByIdNumber.bind(this.calcSrv);
  compareByIdBoolean = this.calcSrv.compareByIdBoolean.bind(this.calcSrv);

  canSeeTrace = false;
  public yesNoValues = YES_NO_VALUES;

  private vmMap = {
    axa: this.accidentsAxa,
    surne: this.accidentsSurne,
    prebal: this.accidentsPrebal
  } as const;

  constructor() {
    this.form = this.fb.group({
      accidentsAxa: this.accidentsAxa.form,
      accidentsSurne: this.accidentsSurne.form,
      accidentsPrebal: this.accidentsPrebal.form
    });

    this.canSeeTrace = this.sharedSrv.getCanSeeTrace();

    const axaHivCtrl = this.accidentsAxa.form.get('aAxaCapitalHIVHepatitisInfection');
    if (axaHivCtrl?.enabled) {
      axaHivCtrl.disable({ emitEvent: false });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public showTrace(vm: CalculatorProductViewModel): boolean {
    return this.canSeeTrace && vm.hasAnyPositiveNumeric();
  }

  public shouldShowZeroState(vm: CalculatorProductViewModel): boolean {
    return !vm.hasAnyPositiveNumeric();
  }

  public getMessages(vm: CalculatorProductViewModel): IResponseMessage[] {
    return vm.hasAnyPositiveNumeric() ? (vm.messages ?? []) : [];
  }

  public getTraceHtml(vm: CalculatorProductViewModel): SafeHtml | '' {
    if (!this.showTrace(vm) || !vm.trace) { return ''; }
    const normalized = normalizeTraceHtml(vm.trace);
    return this.sanitizer.bypassSecurityTrustHtml(normalized);
  }

  public initFromViewModel(vm: ProjectViewModel['calculatorAccidents']): void {
    const model = this.sharedSrv.getModel();
    if (!model) { throw new Error('ProjectViewModel not available'); }
    this.project = model;

    const er = this.project.edit as IEditRuntimeMinimal;

    const professionId: number | undefined
      = toNumber(er.projectClientProfessionCode) ?? undefined;

    const regime: string | undefined
      = (er.projectActivity ?? '').trim() || undefined;

    const clientId: number | undefined
      = toNumber((this.project as any)?.clientId)
      ?? toNumber(er.projectClientId)
      ?? undefined;

    (Object.entries(this.vmMap) as Array<[keyof typeof this.vmMap, CalculatorProductViewModel]>)
      .forEach(([key, child]) => {
        const src = vm[key];
        child.form.patchValue(src.form.getRawValue(), { emitEvent: false });

        child.bonus = src.bonus ?? 0;
        child.chargeable = !!src.chargeable;
        child.productName = src.productName ?? '';
        child.trace = src.trace ?? '';
        child.maximumCapital = src.maximumCapital ?? 0;
        child.summaryCapitals = src.summaryCapitals ?? '';
        child.messages = Array.isArray(src.messages) ? [...src.messages] : [];
      });

    const rawPrebal = vm.prebal.form.getRawValue();
    this.accidentsPrebal.form
      .get('capitalHealthCareAccidents')!
      .setValue(rawPrebal.capitalHealthCareAccidents, { emitEvent: false });

    const contagions$ = (accidentType: 1 | 2 | 3) => {
      if (!clientId || !regime) { return of(false); }
      return this.calcSrv.getAccidentContagions(clientId, accidentType, regime);
    };

    const axaDeath = Number(this.accidentsAxa.form.get('aAxaCapitalDeath')?.value) || 0;
    const axaDisability = Number(this.accidentsAxa.form.get('aAxaCapitalDisability')?.value) || 0;
    const axaHivCapital$ = this.calcSrv.getAxaHivHepatitisCapital(axaDeath, axaDisability);

    this.uiBlocker.block();
    forkJoin({
      axaInvalidities: this.calcSrv.getMasterValues('S_AXA_CAPITALDISABILITYTYPE', { professionId, regime }),
      axaFamilyAssistance: this.calcSrv.getMasterValues('S_AXA_CAPITALFAMILYACCIDENTASSISTANCE', { professionId, regime }),
      axaHivCapital: axaHivCapital$,

      surneInvalidities: this.calcSrv.getMasterValues('S_SURNE_CAPITALABSOLUTEDISABILITYTYPE', { professionId, regime }),
      surneMedicalAssistance: this.calcSrv.getSSurneCapitalHealthDisabilityType(),
      surneHivHepatitis: this.calcSrv.getSSurneHIVHepatitis(),

      prebalInvalidities: this.calcSrv.getMasterValues('S_PREBAL_CAPITALDISABILITYTYPE', { professionId, regime }),
      prebalHasSelectedCapitalDisability: this.calcSrv.getMasterValues('S_PREBAL_HASSELECTEDCAPITALDISABILITY', { professionId, regime }),
      prebalHealthCareAccidents: this.calcSrv.getMasterValues('S_PREBAL_CAPITALHEALTHCAREACCIDENTS', { professionId, regime }),
      prebalHivHepatitis: this.calcSrv.getSPrebalHIVHepatitis(),

      showHivAxa: contagions$(1),
      showHivSurne: contagions$(2),
      showHivPrebal: contagions$(3)
    })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.uiBlocker.unblock();
          this.cdr.markForCheck();
        })
      )
      .subscribe(res => {
        this.showHivAxa = !!res.showHivAxa;
        this.showHivSurne = !!res.showHivSurne;
        this.showHivPrebal = !!res.showHivPrebal;

        this.accidentsAxa.setMasterValues('S_AXA_CAPITALDISABILITYTYPE', res.axaInvalidities);
        this.accidentsAxa.setMasterValues('S_AXA_CAPITALFAMILYACCIDENTASSISTANCE', res.axaFamilyAssistance);

        this.accidentsAxa.setMasterValues('S_AXA_CAPITALHIVHEPATITISINFECTION', res.axaHivCapital);
        this.syncAxaHivControlFromMaster(res.axaHivCapital);

        this.accidentsSurne.setMasterValues('S_SURNE_CAPITALABSOLUTEDISABILITYTYPE', res.surneInvalidities);
        this.accidentsSurne.setMasterValues('S_SURNE_CAPITALHEALTHDISABILITYTYPE', res.surneMedicalAssistance);
        this.accidentsSurne.setMasterValues('S_SURNE_CAPITALHIVHEPATITISINFECTION', res.surneHivHepatitis);

        this.accidentsPrebal.setMasterValues('S_PREBAL_CAPITALDISABILITYTYPE', res.prebalInvalidities);
        this.accidentsPrebal.setMasterValues('S_PREBAL_HASSELECTEDCAPITALDISABILITY', res.prebalHasSelectedCapitalDisability);
        this.accidentsPrebal.setMasterValues('S_PREBAL_CAPITALHEALTHCAREACCIDENTS', res.prebalHealthCareAccidents);
        this.accidentsPrebal.setMasterValues('S_PREBAL_CAPITALHIVHEPATITISINFECTION', res.prebalHivHepatitis);

        this.onInputChangeAAxa();
        this.onInputChangeASurne();
        this.onInputChangeAPrebal();

        this.setupListeners();
      });
  }

  public flushToViewModel(): void {
    const vm = this.sharedSrv.getModel()!.calculatorAccidents;
    (Object.entries(this.vmMap) as Array<[keyof typeof this.vmMap, CalculatorProductViewModel]>)
      .forEach(([key, child]) => {
        const tgt = vm[key];

        tgt.form.patchValue(child.form.getRawValue(), { emitEvent: false });

        tgt.bonus = child.bonus ?? 0;
        tgt.chargeable = !!child.chargeable;
        tgt.productName = child.productName ?? '';
        tgt.trace = child.trace ?? '';
        tgt.maximumCapital = child.maximumCapital ?? 0;
        tgt.summaryCapitals = child.summaryCapitals ?? '';
        tgt.messages = Array.isArray(child.messages) ? [...child.messages] : [];
      });
  }

  private setupListeners(): void {
    const hook = (child: CalculatorProductViewModel, fn: () => void) => {
      child.changes$
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => fn());
    };

    hook(this.accidentsAxa, () => this.onInputChangeAAxa());
    hook(this.accidentsSurne, () => this.onInputChangeASurne());
    hook(this.accidentsPrebal, () => this.onInputChangeAPrebal());

    this.setupAxaHivDependencyListeners();
  }

  private setupAxaHivDependencyListeners(): void {
    const form = this.accidentsAxa.form;

    const deathCtrl = form.get('aAxaCapitalDeath');
    const disabilityCtrl = form.get('aAxaCapitalDisability');

    if (!deathCtrl || !disabilityCtrl) { return; }

    const refresh = () => this.refreshAxaHivFromBackend();

    deathCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(refresh);
    disabilityCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(refresh);
  }

  private refreshAxaHivFromBackend(): void {
    const form = this.accidentsAxa.form;

    const death = Number(form.get('aAxaCapitalDeath')?.value) || 0;
    const disability = Number(form.get('aAxaCapitalDisability')?.value) || 0;

    if (!this.showHivAxa) {
      this.accidentsAxa.setMasterValues('S_AXA_CAPITALHIVHEPATITISINFECTION', []);
      this.syncAxaHivControlFromMaster([]);
      return;
    }

    this.calcSrv.getAxaHivHepatitisCapital(death, disability)
      .pipe(takeUntil(this.destroy$))
      .subscribe(list => {
        this.accidentsAxa.setMasterValues('S_AXA_CAPITALHIVHEPATITISINFECTION', list);
        this.syncAxaHivControlFromMaster(list);
        this.cdr.markForCheck();
      });
  }

  private syncAxaHivControlFromMaster(list: Array<{ id: string; description: string }>): void {
    const ctrl = this.accidentsAxa.form.get('aAxaCapitalHIVHepatitisInfection');
    if (!ctrl) { return; }

    if (ctrl.enabled) {
      ctrl.disable({ emitEvent: false });
    }

    const next = list?.[0]?.id ?? '';
    if (next !== undefined && next !== null) {
      if ((ctrl.value ?? '') !== next) {
        ctrl.patchValue(next, { emitEvent: false });
      }
    }
  }

  private recalc(obs$: Observable<unknown>): void {
    this.uiBlocker.block();
    obs$
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.uiBlocker.unblock();
          this.cdr.markForCheck();
        })
      )
      .subscribe();
  }

  public onInputChangeAAxa(): void {
    this.recalc(
      CalculatorMapper.calcAccidentsAxa(
        this.project,
        this.accidentsAxa,
        (req: ICalculatorAAxaRequest) => this.calcSrv.resultBonusAAxa(req)
      )
    );
  }

  public onInputChangeASurne(): void {
    this.recalc(
      CalculatorMapper.calcAccidentsSurne(
        this.project,
        this.accidentsSurne,
        (req: ICalculatorASurneRequest) => this.calcSrv.resultBonusASurne(req)
      )
    );
  }

  public onInputChangeAPrebal(): void {
    this.recalc(
      CalculatorMapper.calcAccidentsPrebal(
        this.project,
        this.accidentsPrebal,
        (req: ICalculatorAPrebalRequest) => this.calcSrv.resultBonusAPrebal(req)
      )
    );
  }
}
